import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Images, ArrowRight, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal, CreditChip } from "@/components/site/primitives";
import { HISTORY_TIMELINE } from "@/content/history";
import { scrollToId } from "@/lib/scrollTo";
import { cn } from "@/lib/utils";

/* ---- Per-milestone photo gallery (lightbox + thumbnails + keyboard) ---- */
const MilestoneGalleryDialog = ({ item, open, onOpenChange }) => {
  const [index, setIndex] = useState(0);
  const photos = item?.photos || [];
  const total = photos.length;

  useEffect(() => { if (open) setIndex(0); }, [open, item]);

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev]);

  if (!item) return null;
  const current = photos[index];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="history-gallery-dialog" className="max-w-5xl overflow-hidden border-[var(--wf-border)] bg-[var(--wf-paper)] p-0 [&>button]:hidden rounded-sm">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-[var(--wf-border)] px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--wf-green-text)]">Milestone &middot; {item.year}</p>
            <DialogTitle className="font-serif text-lg font-semibold leading-snug text-[var(--wf-ink)]">{item.text}</DialogTitle>
            <DialogDescription className="sr-only">Photo gallery for {item.year}. Photo {index + 1} of {total}.</DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close gallery"
            data-testid="history-gallery-close-button"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--wf-border)] bg-[var(--wf-cream)] text-[var(--wf-ink)] transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Main image */}
        <div className="relative bg-[var(--wf-soil)]">
          <img src={current?.src} alt={current?.alt} className="max-h-[58vh] w-full object-contain" />
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous photo"
                data-testid="history-gallery-prev-button"
                className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(255,253,248,0.92)] text-[var(--wf-ink)] shadow-md transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next photo"
                data-testid="history-gallery-next-button"
                className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(255,253,248,0.92)] text-[var(--wf-ink)] shadow-md transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </>
          )}
          {total > 1 && (
            <span className="absolute bottom-3 right-3 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">{index + 1} / {total}</span>
          )}
        </div>

        {/* Caption + credit */}
        <div className="flex items-center justify-between gap-3 px-5 pt-4">
          <p className="text-sm text-muted-foreground">{current?.caption || current?.alt}</p>
          <CreditChip src={current?.src} className="shrink-0 text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm" />
        </div>

        {/* Thumbnails */}
        {total > 1 && (
          <div className="flex gap-2 overflow-x-auto px-5 pb-5 pt-3">
            {photos.map((p, i) => (
              <button
                key={p.src + i}
                type="button"
                onClick={() => setIndex(i)}
                data-testid={`history-gallery-thumb-${i}`}
                aria-label={`View photo ${i + 1}`}
                aria-current={i === index ? "true" : undefined}
                className={cn(
                  "relative h-16 w-24 shrink-0 overflow-hidden rounded-sm border-2 transition-[border-color,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  i === index ? "border-[var(--wf-maroon)] opacity-100" : "border-transparent opacity-60 hover:opacity-100"
                )}
              >
                <img src={p.src} alt="" aria-hidden="true" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="pb-5" />
      </DialogContent>
    </Dialog>
  );
};

/* ---- A single milestone card (old era-card design) ---- */
const Milestone = ({ item, leftSide, onOpen }) => {
  const [open, setOpen] = useState(false);
  const photos = item.photos || [];
  const hasPhotos = photos.length > 0;
  const panelId = `history-${item.year}-detail`;

  return (
    <li id={`year-${item.year}`} className="relative scroll-mt-28 lg:grid lg:grid-cols-2 lg:gap-16 lg:py-8">
      {/* Node dot */}
      <span
        aria-hidden="true"
        className="absolute left-4 top-3 z-10 h-4 w-4 -translate-x-1/2 rounded-full border-4 border-[var(--wf-cream)] bg-[var(--wf-maroon)] lg:left-1/2 lg:top-10"
      />

      <div className={cn("pl-12 lg:pl-0", leftSide ? "lg:col-start-1 lg:pr-14" : "lg:col-start-2 lg:pl-14")}>
        <Reveal>
          <article className="group overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-3 shadow-[var(--wf-shadow-sm)] transition-shadow duration-300 hover:shadow-[var(--wf-shadow)]">
            {hasPhotos && (
              <button
                type="button"
                onClick={() => onOpen(item)}
                data-testid={`history-photo-${item.year}`}
                aria-label={`Open photo gallery for ${item.year}`}
                className="relative block w-full overflow-hidden rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              >
                <img
                  src={photos[0].src}
                  alt={photos[0].alt}
                  loading="lazy"
                  className="aspect-[16/10] w-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105"
                />
                <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <span className="absolute left-3 top-3 rounded-full bg-[var(--wf-maroon)] px-3 py-1 font-serif text-sm font-semibold text-white">{item.year}</span>
                {photos.length > 1 && (
                  <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-[var(--wf-soil)] backdrop-blur-sm">
                    <Images className="h-3.5 w-3.5" aria-hidden="true" />
                    {photos.length} photos
                  </span>
                )}
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-[var(--wf-farm-green)] px-3 py-1 text-[11px] font-semibold text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  View gallery
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </button>
            )}

            <div className="px-3 pb-3 pt-4">
              <h3 className="font-serif text-xl font-semibold text-[var(--wf-ink)]">{item.text}</h3>

              {item.detail && (
                <>
                  <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    data-testid={`history-explore-${item.year}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                  >
                    {open ? "Show less" : "Read more"}
                    {open ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
                  </button>
                  {open && <p id={panelId} className="mt-3 text-sm leading-6 text-muted-foreground">{item.detail}</p>}
                </>
              )}

              {hasPhotos && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => onOpen(item)}
                    data-testid={`history-explore-photos-${item.year}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[var(--wf-wheat)] px-4 py-2 text-xs font-semibold text-[var(--wf-maroon)] transition-colors duration-200 hover:bg-[var(--wf-maroon)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                  >
                    <Images className="h-3.5 w-3.5" aria-hidden="true" />
                    Explore photos
                  </button>
                </div>
              )}
            </div>
          </article>
        </Reveal>
      </div>
    </li>
  );
};

/* ---- Living timeline ---- */
export const HistoryTimeline = () => {
  const reduce = useReducedMotion();
  const [openItem, setOpenItem] = useState(null);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start 65%", "end 85%"] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="timeline" aria-labelledby="timeline-heading" className="wf-section bg-[var(--wf-cream)]">
      <div className="wf-container">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">
            Milestones
          </span>
          <h2 id="timeline-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            A living timeline
          </h2>
          <p className="mt-3 text-base leading-7 text-muted-foreground">
            Four decades of growth, one year at a time &mdash; select any date or &ldquo;Explore photos&rdquo; to open its gallery.
          </p>
        </Reveal>

        {/* Decade quick-nav */}
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {HISTORY_TIMELINE.map((e) => (
            <button
              key={e.year}
              type="button"
              onClick={() => scrollToId(`year-${e.year}`)}
              data-testid={`history-jump-${e.year}`}
              className="rounded-full border border-[var(--wf-border)] bg-[var(--wf-paper)] px-4 py-2 text-xs font-semibold text-[var(--wf-soil)] transition-colors duration-200 hover:border-[var(--wf-maroon)] hover:bg-[var(--wf-maroon)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              {e.year}
            </button>
          ))}
        </div>

        {/* Timeline body */}
        <div ref={containerRef} className="relative mt-14">
          <div aria-hidden="true" className="absolute left-4 top-0 h-full w-0.5 bg-[var(--wf-border)] lg:left-1/2 lg:-translate-x-1/2" />
          <motion.div
            aria-hidden="true"
            style={reduce ? { scaleY: 1 } : { scaleY: lineScale }}
            className="absolute left-4 top-0 h-full w-0.5 origin-top bg-[var(--wf-maroon)] lg:left-1/2 lg:-translate-x-1/2"
          />

          <ol className="space-y-10 lg:space-y-0">
            {HISTORY_TIMELINE.map((item, i) => (
              <Milestone key={item.year} item={item} leftSide={i % 2 === 0} onOpen={setOpenItem} />
            ))}
          </ol>
        </div>
      </div>

      <MilestoneGalleryDialog item={openItem} open={!!openItem} onOpenChange={(v) => { if (!v) setOpenItem(null); }} />
    </section>
  );
};
